import type { FC } from 'react'
import { useCallback, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { addDays } from 'date-fns'
import { VpnKey } from '@mui/icons-material'
import { CopyButton, WizardHeader } from '@/common/components'
import {
  useApiKeyForm,
  useApiKeyGeneration,
  EXPIRATION_OPTIONS,
  ENVIRONMENT_OPTIONS,
  expirationToIsoTimestamp,
} from '@/features/api-key-generation'
import type { ApiKeyResponse } from '@/features/api-key-generation'
import { useUserInitializationContext } from '@/features/user'
import { useOnboardingUpdate } from '../../hooks'

interface ApiKeySetupStepProps {
  readonly onStepComplete: () => void
}

type StepPhase = 'form' | 'display' | 'saving'

export const ApiKeySetupStep: FC<ApiKeySetupStepProps> = ({ onStepComplete }) => {
  const { updateOnboardingStep } = useUserInitializationContext()
  const { generateAsync, isGenerating, error: generateError } = useApiKeyGeneration()
  const {
    updateStepAsync,
    isUpdating,
    error: onboardError,
    reset: resetOnboard,
  } = useOnboardingUpdate()

  const [phase, setPhase] = useState<StepPhase>('form')
  const [generatedKey, setGeneratedKey] = useState<ApiKeyResponse | null>(null)
  const [onboardErrorMessage, setOnboardErrorMessage] = useState<string | null>(null)

  const {
    name,
    environment,
    expirationDays,
    customDate,
    touched,
    validation,
    isFormValid,
    handleNameChange,
    handleEnvironmentChange,
    handleExpirationChange,
    handleCustomDateChange,
  } = useApiKeyForm()

  const handleGenerate = useCallback(async () => {
    if (!isFormValid) return

    const expiresAt = expirationToIsoTimestamp(expirationDays, customDate)
    const request = {
      name,
      environment,
      ...(expiresAt && { expiresAt }),
    }

    const result = await generateAsync(request)
    if (result.success) {
      setGeneratedKey(result.data)
      setPhase('display')
    }
  }, [isFormValid, name, environment, expirationDays, customDate, generateAsync])

  const handleContinue = useCallback(async () => {
    setPhase('saving')
    setOnboardErrorMessage(null)

    try {
      const result = await updateStepAsync({ apiKeyGenerated: true })

      if (result && typeof result === 'object' && 'success' in result && result.success === false) {
        setOnboardErrorMessage('API key created, but progress could not be saved.')
        setPhase('display')
        return
      }

      updateOnboardingStep('apiKeyGenerated')
      onStepComplete()
    } catch {
      setOnboardErrorMessage('API key created, but progress could not be saved.')
      setPhase('display')
    }
  }, [updateStepAsync, updateOnboardingStep, onStepComplete])

  const handleSkip = useCallback(() => {
    onStepComplete()
  }, [onStepComplete])

  const handleRetryOnboard = useCallback(() => {
    resetOnboard()
    setOnboardErrorMessage(null)
  }, [resetOnboard])

  const isDisabled = isGenerating || isUpdating || phase === 'saving'
  const displayError = generateError ?? onboardErrorMessage ?? onboardError

  const showDisplayView = (phase === 'display' || phase === 'saving') && generatedKey !== null

  if (showDisplayView) {
    return (
      <Box sx={containerSx}>
        <Typography variant="h6" sx={titleSx}>
          API Key Generated
        </Typography>

        <Alert severity="warning" sx={alertSx}>
          Copy your API key now. You will not be able to see it again.
        </Alert>

        <Box sx={keyContainerSx}>
          <TextField
            value={generatedKey.apiKey}
            fullWidth
            InputProps={{ readOnly: true, sx: keyInputSx }}
          />
          <CopyButton text={generatedKey.apiKey} tooltipText="Copy API key" />
        </Box>

        {displayError && (
          <Alert
            severity="error"
            sx={alertSx}
            action={
              onboardErrorMessage ? (
                <Button color="inherit" size="small" onClick={handleRetryOnboard}>
                  Retry
                </Button>
              ) : undefined
            }
          >
            {displayError}
          </Alert>
        )}

        {phase === 'saving' && !displayError && (
          <Box sx={savingContainerSx}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Saving progress...
            </Typography>
          </Box>
        )}

        <Box sx={actionsSx}>
          <Button
            variant="contained"
            onClick={handleContinue}
            disabled={isUpdating || phase === 'saving'}
            startIcon={isUpdating ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Continue
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      <WizardHeader
        icon={<VpnKey />}
        title="Generate an API Key"
        description="API keys authenticate your daemon with the notification service."
      />

      <Box sx={containerSx}>
        <TextField
          label="Key Name"
          value={name}
          onChange={handleNameChange}
          error={touched && !validation.isValid}
          helperText={
            touched ? validation.error : 'A descriptive name for your API key (e.g., Dev Laptop)'
          }
          fullWidth
          disabled={isDisabled}
          sx={fieldSx}
        />

        <FormControl fullWidth sx={fieldSx} disabled={isDisabled}>
          <InputLabel>Environment</InputLabel>
          <Select value={environment} label="Environment" onChange={handleEnvironmentChange}>
            {ENVIRONMENT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={fieldSx} disabled={isDisabled}>
          <InputLabel>Expiration</InputLabel>
          <Select
            value={expirationDays === null ? 'null' : String(expirationDays)}
            label="Expiration"
            onChange={handleExpirationChange}
          >
            {EXPIRATION_OPTIONS.map((option) => (
              <MenuItem
                key={String(option.value)}
                value={option.value === null ? 'null' : String(option.value)}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {expirationDays === 'custom' && (
          <DatePicker
            label="Expiration Date"
            value={customDate}
            onChange={handleCustomDateChange}
            minDate={addDays(new Date(), 1)}
            disabled={isDisabled}
            slotProps={{
              textField: {
                fullWidth: true,
                sx: fieldSx,
                helperText: 'Pick a date using the calendar icon',
              },
            }}
          />
        )}

        {expirationDays === null && (
          <Alert severity="warning" sx={alertSx}>
            API keys without an expiration date pose a security risk. Consider setting an
            expiration.
          </Alert>
        )}

        {displayError && (
          <Alert severity="error" sx={alertSx}>
            {displayError}
          </Alert>
        )}

        <Box sx={actionsSx}>
          <Button onClick={handleSkip} disabled={isDisabled}>
            Skip for Now
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={!isFormValid || isDisabled}
            startIcon={isGenerating ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {isGenerating ? 'Generating...' : 'Generate Key'}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

const containerSx: SxProps<Theme> = {
  py: 2,
}

const titleSx: SxProps<Theme> = {
  fontWeight: 600,
  mb: 2,
}

const fieldSx: SxProps<Theme> = {
  mb: 3,
}

const alertSx: SxProps<Theme> = {
  mb: 2,
}

const keyContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  mb: 3,
}

const keyInputSx: SxProps<Theme> = {
  fontFamily: 'monospace',
  fontSize: '0.875rem',
}

const savingContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1,
  mb: 2,
}

const actionsSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  mt: 4,
}
